import React from 'react';
import { type CustomComponentRenderErrorPayload } from './CustomComponentProvider';

interface Props {
  slug: string;
  sourceHash: string;
  isEditor: boolean;
  onRenderError: ((payload: CustomComponentRenderErrorPayload) => void) | null;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class CustomComponentErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    this.props.onRenderError?.({
      slug: this.props.slug,
      sourceHash: this.props.sourceHash,
      errorName: error.name || 'Error',
      errorMessage: error.message || String(error),
      stack: error.stack,
    });
    console.error(`[custom-component:${this.props.slug}] render failed:`, error);
  }

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    if (!this.props.isEditor) {
      // Hide on published pages; the host still gets the error via `onRenderError`.
      return (
        <div
          data-custom-component-error={this.props.slug}
          data-custom-component-slug={this.props.slug}
          data-custom-component-source-hash={this.props.sourceHash}
          data-custom-component-status="error"
          data-custom-component-error-message={error.message}
        />
      );
    }
    return (
      <div
        data-custom-component-error={this.props.slug}
        style={{
          padding: '0.75rem 1rem',
          color: '#6b7280',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          fontSize: '0.8125rem',
          background: '#f9fafb',
          border: '1px dashed #d1d5db',
          borderRadius: '0.375rem',
        }}
      >
        <div style={{ fontWeight: 500, color: '#374151' }}>
          {`Component "${this.props.slug}" couldn’t be rendered.`}
        </div>
        <details style={{ marginTop: '0.5rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.75rem' }}>Details</summary>
          <div
            style={{
              marginTop: '0.375rem',
              fontFamily: 'ui-monospace, monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {`${error.name || 'Error'}: ${error.message || String(error)}`}
          </div>
        </details>
      </div>
    );
  }
}
